import React from 'react';
import { Img } from '../ContentTile/styles';

const layoutAssetPreferences = {
  'Irregular - 2x1 wide': {
    hero: 'landscape',
    topRight: 'square',
    bottomSupport: 'landscape',
  },
  'Irregular - 1x2 split row': {
    lead: 'landscape',
    support: 'landscape',
  },
  'Irregular - 2x1 split column right': {
    lead: 'square',
  },
  'Irregular - 2x1 split column left': {
    lead: 'square',
  },
};

const preferredLandscapeLayouts = {
  'Basic - 2x1': new Set(['standard', 'short']),
  'Basic - 2x2': new Set(['standard']),
  'Basic - 3x1': new Set(['standard', 'short']),
  'Irregular - 1x2 split row': new Set(['tall']),
};

const layoutRatioHints = {
  'Basic - 2x1': {
    tall: 1,
    standard: 16 / 9,
    short: 2,
  },
  'Basic - 3x1': {
    tall: 0.8,
    standard: 1,
    short: 4 / 3,
  },
  'Basic - 2x2': {
    tall: 1,
    standard: 4 / 3,
  },
  'Irregular - 2x1 wide': {
    tall: 4 / 3,
    standard: 16 / 9,
    short: 2,
  },
  'Irregular - 2x1 split column right': {
    tall: 1,
    standard: 16 / 9,
    short: 2,
  },
  'Irregular - 2x1 split column left': {
    tall: 1,
    standard: 16 / 9,
    short: 2,
  },
  'Irregular - 1x2 split row': {
    tall: 16 / 9,
  },
};

const squareishThreshold = 1.35;

const mapBreakpointsToSources = (srcSet) =>
  Array.isArray(srcSet)
    ? srcSet.reduce((acc, item) => {
        acc[item.bp] = item.src;
        return acc;
      }, {})
    : {};

const getRatioHint = ({ width, height, layoutName, layoutSize }) => {
  if (width > 0 && height > 0) return width / height;
  return layoutRatioHints[layoutName]?.[layoutSize] || 1;
};

const getAssetPreference = ({ layoutName, layoutSize, layoutSlot, width, height }) => {
  const measuredRatio = width > 0 && height > 0 ? width / height : null;

  if (measuredRatio !== null && measuredRatio >= squareishThreshold) {
    return 'landscape';
  }

  const slotPreference = layoutAssetPreferences[layoutName]?.[layoutSlot];

  if (slotPreference) return slotPreference;

  if (layoutName === 'Irregular - 1x2 split row') {
    return 'landscape';
  }

  if (layoutName && layoutSize && preferredLandscapeLayouts[layoutName]?.has(layoutSize)) {
    return 'landscape';
  }

  return getRatioHint({ width, height, layoutName, layoutSize }) >= squareishThreshold
    ? 'landscape'
    : 'square';
};

function ResponsiveImage({
  alt,
  srcSet,
  height,
  width,
  useNewAspectRatioBehavior,
  layoutName,
  layoutSize,
  layoutSlot,
}) {
  const srcs = mapBreakpointsToSources(srcSet);

  const squareishSrc = srcs.xs || srcs.sm || srcs.md || srcs.lg || srcs.xl || srcs.xxl || '';
  const landscapeSrc = srcs.xl || srcs.xxl || srcs.lg || srcs.md || squareishSrc;
  const useLandscapeAsset =
    getAssetPreference({ layoutName, layoutSize, layoutSlot, width, height }) === 'landscape';
  const selectedSrc = useLandscapeAsset ? landscapeSrc : squareishSrc;

  if (useNewAspectRatioBehavior) {
    return (
      <Img
        style={{
          objectFit: 'cover',
          objectPosition: 'center',
          width: '100%',
          height: '100%',
        }}
        src={selectedSrc}
        alt={alt}
        loading="lazy"
        width={width}
        height={height}
      />
    );
  }

  return (
    <picture>
      <source
        srcSet={`
          ${srcs.xs} 390w,
          ${srcs.sm} 770w,
          ${srcs.md} 1130w
        `}
        media="(max-width: 767px) and (orientation: portrait)"
        type="image/webp"
        sizes="100vw"
      />

      <source
        srcSet={`
          ${srcs.xl || srcs.md} 1536w,
          ${srcs.xxl || srcs.md} 2208w
        `}
        media="(min-width: 768px) and (orientation: landscape)"
        type="image/webp"
        sizes="(min-width:1130px) 1130px, 100vw"
      />

      <Img
        style={{ objectFit: 'cover' }}
        src={`${srcs.xs}`}
        alt={alt}
        loading="lazy"
        width={width}
        height={height}
      />
    </picture>
  );
}

export default ResponsiveImage;
